// Web push registration + foreground handling (.kiro/specs/1598-chat-push-notifications-web).
// Background pushes (tab not focused) are handled entirely by the service
// worker (public/firebase-messaging-sw.js) — this file only covers what
// needs the main app context: asking for permission, registering the token,
// and reacting to a push that arrives while a tab IS focused.
import { arrayUnion, doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { deleteToken, getMessaging, getToken, isSupported, onMessage } from 'firebase/messaging';

import { app, db, firebaseConfig } from './firebase';

const VAPID_KEY = process.env.REACT_APP_FIREBASE_VAPID_KEY;
const ASKED_STORAGE_KEY = 'gyPushAsked';

// EARS 3: ask once — respect the browser's own re-prompt suppression after a
// decline instead of nagging on every page load. A prior "granted" is also
// short-circuited (nothing to ask).
const alreadyAsked = () => {
  try {
    return localStorage.getItem(ASKED_STORAGE_KEY) === '1';
  } catch {
    return false; // storage unavailable (private mode) — fail open, ask once, worst case
  }
};

const markAsked = () => {
  try {
    localStorage.setItem(ASKED_STORAGE_KEY, '1');
  } catch {
    // quota/unavailable — cosmetic, never block the flow
  }
};

const saveToken = async (userId, token) => {
  await setDoc(doc(db, 'deviceTokens', String(userId)), { tokens: arrayUnion({ token, updatedAt: Date.now() }) }, { merge: true });
};

// EARS 2/3. Returns the registered token, or null if permission wasn't
// granted / push isn't supported / the VAPID key isn't configured yet
// (see .kiro/specs/1598-chat-push-notifications-web/requirements.md's
// Context — the VAPID key is a manual, Console-only, one-time setup step).
export const requestPushPermission = async userId => {
  if (!userId || !VAPID_KEY || typeof Notification === 'undefined' || !(await isSupported())) return null;
  if (alreadyAsked()) return null;
  markAsked();

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  // Service workers can't read process.env — pass the config the worker
  // needs over the query string instead of hardcoding real project values
  // into a checked-in static file (public/firebase-messaging-sw.js reads it
  // back via self.location.search).
  const swUrl = `/firebase-messaging-sw.js?config=${encodeURIComponent(JSON.stringify(firebaseConfig))}`;
  const registration = await navigator.serviceWorker.register(swUrl);

  const messaging = getMessaging(app);
  const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
  if (!token) return null;

  await saveToken(userId, token);
  return token;
};

// Security-relevant, not just cleanup: an FCM token is registered per
// BROWSER/device (via its service worker), not per logged-in user — it has
// no idea who's currently authenticated in the app. If user A grants
// permission, then logs out and user B logs into the SAME browser, nothing
// about that token changes on its own: a message to A still reaches this
// exact device's OS notification tray, regardless of who's using it now,
// because deviceTokens/A still lists it and nothing ever invalidated it.
// Call this BEFORE signOut(auth) (useFirebaseAuthBridge.js) — the removal
// write to deviceTokens/{userId} needs request.auth.uid === userId, which is
// only still true while the outgoing user's Firebase session is live.
export const clearPushToken = async userId => {
  if (!userId || typeof Notification === 'undefined' || !(await isSupported())) return;
  try {
    const messaging = getMessaging(app);
    const currentToken = await getToken(messaging, { vapidKey: VAPID_KEY }).catch(() => null);
    if (currentToken) {
      const ref = doc(db, 'deviceTokens', String(userId));
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const tokens = (snap.data().tokens || []).filter(t => t.token !== currentToken);
        await updateDoc(ref, { tokens });
      }
      await deleteToken(messaging);
    }
  } catch {
    // Best-effort — never block logout over this.
  }
  try {
    // So the NEXT person to log in on this device gets asked for permission
    // (and registers their OWN token) instead of silently inheriting the
    // previous user's already-asked state.
    localStorage.removeItem(ASKED_STORAGE_KEY);
  } catch {
    // quota/unavailable — cosmetic, never block the flow
  }
};

// EARS 5: foreground messages (tab focused) — the caller (App.js) feeds this
// into the SAME toast useUnreadMessages.js already shows, not a second,
// parallel notification UI. Deliberately does not itself decide whether to
// suppress for the active conversation — that's the caller's job, same
// window.activeChatId convention #1596 established.
export const listenForForegroundPush = callback => {
  if (typeof Notification === 'undefined') return () => {};
  const messaging = getMessaging(app);
  return onMessage(messaging, payload => callback(payload));
};
